import { Box } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"

import { GoodsReceiptDetailsTotals } from "../../../../components"
import { useGoodsReceiptContext } from "../../../../contexts"
import type { GoodsReceiptTotals } from "../../../../types"
import { calculatePurchaseOrderTotalsFromPalletRows } from "../../../../utils"
import { useGetGoodsReceiptPalletRows } from "../../../SoftReceived/hooks"

import { StoredPurchaseOrder } from "./StoredPurchaseOrder"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"

export const StoredPurchaseOrdersTab: FC = () => {
  const { goodsReceipt } = useGoodsReceiptContext()
  const purchaseOrders = useMemo(() => goodsReceipt.orders ?? [], [goodsReceipt.orders])
  const { palletRows: goodsReceiptPalletRows } = useGetGoodsReceiptPalletRows(goodsReceipt.goodsReceiptId)

  const totals: GoodsReceiptTotals = useMemo(() => {
    const allPurchaseOrderLines = purchaseOrders.flatMap((po) => po.orderLines ?? [])
    const goodsReceiptLines = goodsReceipt.productDetails ?? []
    const palletRows = goodsReceiptPalletRows ?? []

    return calculatePurchaseOrderTotalsFromPalletRows(allPurchaseOrderLines, goodsReceiptLines, palletRows)
  }, [purchaseOrders, goodsReceipt.productDetails, goodsReceiptPalletRows])

  if (purchaseOrders.length === 0) {
    return <InformationMissingAlert description='No purchase orders are associated with this goods receipt.' />
  }

  return (
    <Box className='flex min-h-0 grow flex-col gap-y-8 overflow-auto' data-testid='goods-receipt-purchase-orders'>
      <Box className='flex flex-col gap-y-4'>
        {purchaseOrders.map((purchaseOrder) => (
          <StoredPurchaseOrder
            key={purchaseOrder.orderId}
            purchaseOrder={purchaseOrder}
            goodsReceiptLines={goodsReceipt.productDetails ?? []}
            goodsReceiptPalletRows={goodsReceiptPalletRows ?? []}
          />
        ))}
      </Box>
      <GoodsReceiptDetailsTotals label='Total Received' totals={totals} />
    </Box>
  )
}
